/**
 * signatureInk - the studio's signature ink system, shared by the
 * Check Studio and the Document Editor (parity pass, 2026-11-05).
 *
 * Princewill (2026-11-04): "Make sure the signature upload has
 * different color selection to match the exact color not just
 * black." A placed signature has its OWN ink: preset pen inks, a
 * custom colour for an exact match, or "Keep original" - the
 * colour the signature was written in, preserved through the
 * clean. The values below are the Check Studio's shipped presets
 * (components/CheckBuilder.tsx); a pin test keeps the two lists
 * identical so the editors can never drift apart.
 *
 * The derivation is the Check Studio's one derivation: the raw
 * source always goes through SignatureExtractorEngine, which
 * isolates the strokes onto a transparent background and tints
 * them with the chosen ink (or keeps their written colour in
 * Keep-original mode). Library entries stay RAW - the ink is a
 * property of the placement/editor, never of the upload.
 */
import { SignatureExtractorEngine } from '../engines/SignatureExtractorEngine';

export const SIGNATURE_INK_PRESETS: Array<{ name: string; val: string }> = [
    { name: 'Black', val: '#111111' },
    { name: 'Ball-pen blue-black', val: '#1b2440' },
    { name: 'Blue', val: '#0047ab' },
    { name: 'Navy', val: '#003366' },
    { name: 'Red', val: '#cc0000' },
    { name: 'Green', val: '#006400' }
];

export const DEFAULT_SIGNATURE_INK = '#1b2440';

/** Snapshot/custom values land untrusted: only a plain #rrggbb hex
 * is a usable ink; anything else falls back to the default
 * ball-pen ink rather than breaking the colour input. */
export const normalizeSignatureInk = (v: any): string =>
    typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v) ? v.toLowerCase() : DEFAULT_SIGNATURE_INK;

/** The extractor options for an ink state. A chosen ink tints;
 * Keep-original preserves the written colour. */
export const signatureInkProcessOptions = (inkColor: string, inkOriginal: boolean): {
    tintColor?: string; keepOriginalColor?: boolean;
} => inkOriginal
    ? { keepOriginalColor: true }
    : { tintColor: normalizeSignatureInk(inkColor) };

/**
 * Derive the placed signature image from its raw source under an
 * ink state - the same derivation the Check Studio runs for every
 * placement and every ink pick, so a placed signature recolours
 * the moment the ink changes. Never throws: on any engine failure
 * the raw source is returned, so an ink pick can never lose the
 * user's signature.
 */
export async function deriveSignatureImage(raw: string, inkColor: string, inkOriginal: boolean): Promise<string> {
    if (!raw) return raw;
    try {
        return await SignatureExtractorEngine.processSignature(raw, signatureInkProcessOptions(inkColor, inkOriginal));
    } catch {
        return raw;
    }
}

// --- Typed signatures (the Lab's Type mode) ---

/** Script faces for typed signatures - the same faces the Check
 * Studio's signature styles write with. */
export const SIGNATURE_TYPE_FACES: Array<{ id: string; name: string; stack: string }> = [
    { id: 'fountain', name: 'Fountain (Alex Brush)', stack: "'Alex Brush', 'Snell Roundhand', 'Apple Chancery', 'Segoe Script', cursive" },
    { id: 'ballpoint', name: 'Ballpoint (Dancing Script)', stack: "'Dancing Script', cursive" },
    { id: 'cursive', name: 'Cursive (Great Vibes)', stack: "'Great Vibes', cursive" },
    { id: 'felt_tip', name: 'Felt tip (Pinyon Script)', stack: "'Pinyon Script', cursive" }
];

export const signatureTypeFace = (id: string): { id: string; name: string; stack: string } =>
    SIGNATURE_TYPE_FACES.find(f => f.id === id) || SIGNATURE_TYPE_FACES[0];

/** Provenance carried on a signature field for a typed signature,
 * so an ink change re-renders the name exactly instead of tinting
 * a raster. `ink` is the colour it was created in - what
 * Keep-original restores for a typed signature. */
export interface TypedSignatureSource {
    kind: 'typed';
    text: string;
    face: string;
    ink: string;
}

export type SignatureCanvasFactory = (width: number, height: number) => HTMLCanvasElement;

const defaultCanvasFactory: SignatureCanvasFactory = (width, height) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    return canvas;
};

/**
 * Rasterize a typed signature: the name in the chosen script face
 * and ink colour on a transparent background. Returns '' for blank
 * text. Pure canvas work - no handwriting engine, no analysis of
 * anyone's writing; it renders text the user typed.
 */
export function renderTypedSignature(
    text: string,
    faceId: string,
    inkColor: string,
    canvasFactory: SignatureCanvasFactory = defaultCanvasFactory
): string {
    const name = (text || '').trim();
    if (!name) return '';
    const face = signatureTypeFace(faceId);
    const fontSize = 64;
    const canvas = canvasFactory(Math.max(240, name.length * 34 + 40), 120);
    const ctx = canvas.getContext('2d');
    if (!ctx) return '';
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `${fontSize}px ${face.stack}`;
    ctx.fillStyle = normalizeSignatureInk(inkColor);
    ctx.textBaseline = 'middle';
    ctx.fillText(name, 16, canvas.height / 2 + 4);
    try {
        return canvas.toDataURL('image/png');
    } catch {
        return '';
    }
}

/** Re-derive a signature field's placed image after an ink change:
 * typed signatures re-render from their text (exact at any ink);
 * uploaded / drawn signatures re-run the extractor derivation
 * from their stored raw source. Fields without a raw source are
 * returned untouched. */
export async function rederiveSignatureValue(
    field: { value: string; rawValue?: string; sigSource?: TypedSignatureSource },
    inkColor: string,
    inkOriginal: boolean,
    canvasFactory: SignatureCanvasFactory = defaultCanvasFactory
): Promise<string> {
    if (field.sigSource && field.sigSource.kind === 'typed') {
        const ink = inkOriginal ? field.sigSource.ink : inkColor;
        return renderTypedSignature(field.sigSource.text, field.sigSource.face, ink, canvasFactory) || field.value;
    }
    if (field.rawValue) {
        return deriveSignatureImage(field.rawValue, inkColor, inkOriginal);
    }
    return field.value;
}

/**
 * Re-derive PLACED library items after an ink change (document
 * editor parity corrective, 2026-11-05). A placed item stores
 * only its derived image plus the id of the library entry it came
 * from; the RAW source lives in that entry (library entries stay
 * raw - see the header), and `resolveRawSource` reads it back.
 * The same derivation as the fields then produces the item's new
 * image, so a signature placed from the library recolours exactly
 * like a signature in a field.
 *
 * Only signature placements are eligible. Placed logos and
 * pictures are returned untouched - byte-identical. A signature
 * placement whose raw source cannot be resolved (its library
 * entry was deleted, or the item was restored from a stripped
 * fallback save with no image kept) is likewise returned
 * untouched: it stays exactly as it is, never half-replaced.
 * Items are reused by reference whenever nothing changes, and
 * the input array itself is returned when no item changed.
 */
export async function rederivePlacedSignatureItems<T extends {
    kind: string; src?: string; assetId?: string; assetKind?: string;
}>(
    items: T[],
    resolveRawSource: (assetId: string) => string | undefined,
    inkColor: string,
    inkOriginal: boolean
): Promise<T[]> {
    let changed = false;
    const derivations = new Map<string, Promise<string>>();
    const out: T[] = [];
    for (const item of items) {
        const eligible = item.kind === 'image'
            && item.assetKind === 'signatures'
            && !!item.assetId
            && !!item.src;
        if (!eligible) { out.push(item); continue; }
        const raw = resolveRawSource(item.assetId!);
        if (!raw) { out.push(item); continue; }
        let derivation = derivations.get(item.assetId!);
        if (!derivation) {
            derivation = deriveSignatureImage(raw, inkColor, inkOriginal);
            derivations.set(item.assetId!, derivation);
        }
        const next = await derivation;
        if (next && next !== item.src) {
            out.push({ ...item, src: next });
            changed = true;
        } else {
            out.push(item);
        }
    }
    return changed ? out : items;
}
